/* eslint-disable default-param-last */
import { Reducer } from 'redux';
import produce from 'immer';
import { CentralIaTypes, CentralIaState, CentralIaCitation } from './types';

const REQUEST_INITIAL_STATE = {
  loading: false,
  loaded: false,
  error: false,
};

const INITIAL_STATE: CentralIaState = {
  messages: [],
  folders: [],
  chats: [],
  pendingFiles: [],
  chatFiles: [],
  currentChatId: null,
  selectedProvider: '',
  selectedModel: '',
  chatStarted: false,
  sidebarCollapsed: false,
  conversationId: null,
  lastError: null,
  request: {
    send_chat_message: REQUEST_INITIAL_STATE,
    generate_image: REQUEST_INITIAL_STATE,
    create_folder: REQUEST_INITIAL_STATE,
    fetch_folders: REQUEST_INITIAL_STATE,
    delete_folder: REQUEST_INITIAL_STATE,
    update_folder: REQUEST_INITIAL_STATE,
    fetch_chats: REQUEST_INITIAL_STATE,
    delete_chat: REQUEST_INITIAL_STATE,
    fetch_chat: REQUEST_INITIAL_STATE,
    update_chat: REQUEST_INITIAL_STATE,
    move_chat: REQUEST_INITIAL_STATE,
    upload_file: REQUEST_INITIAL_STATE,
  },
};

const mapCitations = (citations: any): CentralIaCitation[] =>
  Array.isArray(citations)
    ? citations
        .filter((c: any) => c && typeof c.url === 'string')
        .map((c: any) => ({ url: c.url, title: c.title || null }))
    : [];

const upsertChat = (draft: CentralIaState, chat: any) => {
  if (!chat?.id) return;
  draft.currentChatId = chat.id;
  draft.selectedProvider = chat.provider || draft.selectedProvider;
  draft.selectedModel = chat.model || draft.selectedModel;
  draft.conversationId = chat.conversation_id || draft.conversationId;
  if (!draft.chats) draft.chats = [];
  const existingIdx = draft.chats.findIndex((c) => String(c.id) === String(chat.id));
  if (existingIdx === -1) {
    draft.chats.unshift({
      id: chat.id,
      title: chat.title,
      central_ia_folder_id: chat.central_ia_folder_id,
      conversation_id: chat.conversation_id,
      provider: chat.provider,
      model: chat.model,
      last_message_at: chat.last_message_at,
      created_at: chat.created_at,
      updated_at: chat.updated_at,
    });
  } else {
    draft.chats[existingIdx].last_message_at = chat.last_message_at;
    draft.chats[existingIdx].updated_at = chat.updated_at;
  }
};

const removeStreamingMessage = (draft: CentralIaState) => {
  draft.messages = draft.messages.filter((m) => !m.streaming);
};

const reducer: Reducer<CentralIaState> = (state = INITIAL_STATE, action: any) => {
  const { type, result, payload, folderName, folderId, chatId, chatTitle, targetFolderId } = action;
  return produce(state, (draft) => {
    switch (type) {
      case CentralIaTypes.SET_PROVIDER:
        draft.selectedProvider = payload?.provider || '';
        break;
      case CentralIaTypes.SET_MODEL:
        draft.selectedModel = payload?.model || '';
        break;
      case CentralIaTypes.START_CHAT:
        draft.chatStarted = true;
        draft.messages = [];
        draft.conversationId = null;
        draft.currentChatId = null;
        draft.pendingFiles = [];
        draft.chatFiles = [];
        break;
      case CentralIaTypes.NEW_CHAT:
        draft.chatStarted = false;
        draft.messages = [];
        draft.conversationId = null;
        draft.currentChatId = null;
        draft.pendingFiles = [];
        draft.chatFiles = [];
        break;
      case CentralIaTypes.TOGGLE_SIDEBAR:
        draft.sidebarCollapsed = !state.sidebarCollapsed;
        break;
      case CentralIaTypes.ADD_USER_MESSAGE:
        draft.messages.push({
          id: Date.now(),
          role: 'user',
          content: payload?.content || '',
        });
        break;
      case CentralIaTypes.SEND_CHAT_MESSAGE_REQUEST:
        draft.lastError = null;
        draft.request.send_chat_message = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.CHAT_STREAM_START:
        upsertChat(draft, payload?.chat);
        break;
      case CentralIaTypes.CHAT_STREAM_CHUNK: {
        const last = draft.messages[draft.messages.length - 1];
        if (last && last.role === 'assistant' && last.streaming) {
          last.content += payload?.content || '';
        } else {
          draft.messages.push({
            id: Date.now(),
            role: 'assistant',
            content: payload?.content || '',
            streaming: true,
          });
        }
        break;
      }
      case CentralIaTypes.CHAT_STREAM_DONE: {
        const assistantMessage = payload?.assistantMessage || {};
        upsertChat(draft, payload?.chat);
        removeStreamingMessage(draft);
        draft.messages.push({
          id: assistantMessage.id || Date.now(),
          role: 'assistant',
          content: assistantMessage.content || '',
          citations: mapCitations(assistantMessage.citations),
        });
        if (!draft.chatFiles) draft.chatFiles = [];
        (draft.pendingFiles || []).forEach((file) => {
          const alreadyInChat = draft.chatFiles.some(
            (f) => String(f.file_id) === String(file.file_id),
          );
          if (!alreadyInChat) draft.chatFiles.push(file);
        });
        draft.pendingFiles = [];
        draft.request.send_chat_message = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.CHAT_STREAM_TITLE: {
        const target = (draft.chats || []).find((c) => String(c.id) === String(payload?.chatId));
        if (target && payload?.title) target.title = payload.title;
        break;
      }
      case CentralIaTypes.CHAT_STREAM_ERROR:
        upsertChat(draft, payload?.chat);
        removeStreamingMessage(draft);
        draft.lastError = payload?.message || null;
        draft.request.send_chat_message = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.CHAT_STREAM_STOPPED:
        draft.messages.forEach((m) => {
          if (m.streaming) {
            m.streaming = false;
            m.interrupted = true;
          }
        });
        draft.request.send_chat_message = REQUEST_INITIAL_STATE;
        break;
      case CentralIaTypes.CHAT_FILES_MISSING: {
        const missing = (payload?.fileIds || []).map(String);
        draft.chatFiles = (draft.chatFiles || []).filter(
          (f) => !missing.includes(String(f.file_id)),
        );
        draft.pendingFiles = (draft.pendingFiles || []).filter(
          (f) => !missing.includes(String(f.file_id)),
        );
        break;
      }
      case CentralIaTypes.GENERATE_IMAGE_REQUEST:
        draft.request.generate_image = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.GENERATE_IMAGE_SUCCESS: {
        const data = result?.data || {};
        const first = (Array.isArray(data.data) ? data.data : data.images || [])[0] || {};
        const b64 = first.b64_json || data.b64_json || '';
        const mime = first.mime_type || data.mime_type || 'image/png';
        const imageUrl = data.url || first.url || (b64 ? `data:${mime};base64,${b64}` : '');
        if (imageUrl) {
          draft.messages.push({
            id: Date.now(),
            role: 'assistant',
            content: '',
            imageUrl,
          });
        }
        draft.request.generate_image = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.GENERATE_IMAGE_FAILURE:
        draft.request.generate_image = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.CREATE_FOLDER_REQUEST:
        draft.request.create_folder = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.CREATE_FOLDER_SUCCESS: {
        const folder = result?.data || {};
        if (!draft.folders) draft.folders = [];
        draft.folders.push({
          id: folder.id || Date.now(),
          name: folder.name || folderName || '',
          created_at: folder.created_at,
        });
        draft.request.create_folder = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.CREATE_FOLDER_FAILURE:
        draft.request.create_folder = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.RESET_CREATE_FOLDER:
        draft.request.create_folder = REQUEST_INITIAL_STATE;
        break;
      case CentralIaTypes.FETCH_FOLDERS_REQUEST:
        draft.request.fetch_folders = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.FETCH_FOLDERS_SUCCESS: {
        const list = Array.isArray(result?.data) ? result.data : result?.data?.data || [];
        draft.folders = list.map((folder: any) => ({
          id: folder.id,
          name: folder.name,
          created_at: folder.created_at,
        }));
        draft.request.fetch_folders = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.FETCH_FOLDERS_FAILURE:
        draft.request.fetch_folders = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.DELETE_FOLDER_REQUEST:
        draft.request.delete_folder = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.DELETE_FOLDER_SUCCESS:
        if (!draft.folders) draft.folders = [];
        draft.folders = draft.folders.filter((f) => String(f.id) !== String(folderId));
        draft.request.delete_folder = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case CentralIaTypes.DELETE_FOLDER_FAILURE:
        draft.request.delete_folder = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.UPDATE_FOLDER_REQUEST:
        draft.request.update_folder = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.UPDATE_FOLDER_SUCCESS: {
        if (!draft.folders) draft.folders = [];
        const folder = result?.data || {};
        const idx = draft.folders.findIndex((f) => String(f.id) === String(folderId));
        if (idx !== -1) {
          draft.folders[idx].name = folder.name || folderName || draft.folders[idx].name;
        }
        draft.request.update_folder = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.UPDATE_FOLDER_FAILURE:
        draft.request.update_folder = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.FETCH_CHATS_REQUEST:
        draft.request.fetch_chats = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.FETCH_CHATS_SUCCESS: {
        const list = Array.isArray(result?.data) ? result.data : result?.data?.data || [];
        draft.chats = list;
        draft.request.fetch_chats = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.FETCH_CHATS_FAILURE:
        draft.request.fetch_chats = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.DELETE_CHAT_REQUEST:
        draft.request.delete_chat = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.DELETE_CHAT_SUCCESS:
        if (!draft.chats) draft.chats = [];
        draft.chats = draft.chats.filter((c) => String(c.id) !== String(chatId));
        draft.request.delete_chat = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      case CentralIaTypes.DELETE_CHAT_FAILURE:
        draft.request.delete_chat = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.FETCH_CHAT_REQUEST:
        draft.request.fetch_chat = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.FETCH_CHAT_SUCCESS: {
        const data = result?.data || {};
        const chat = data.chat || {};
        const apiMessages = Array.isArray(data.messages) ? data.messages : [];
        draft.currentChatId = chat.id || null;
        draft.conversationId = chat.conversation_id || null;
        draft.selectedProvider = chat.provider || draft.selectedProvider;
        draft.selectedModel = chat.model || draft.selectedModel;
        draft.chatStarted = true;
        draft.messages = apiMessages.map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content || '',
          citations: mapCitations(msg.citations),
        }));
        const apiFiles = Array.isArray(data.files) ? data.files : chat.files || [];
        draft.chatFiles = apiFiles.map((file: any) => ({
          file_id: file.file_id ?? file.id,
          file_name: file.file_name ?? file.name,
          content_type: file.content_type,
          size_bytes: file.size_bytes,
          s3_url: file.s3_url,
        }));
        draft.pendingFiles = [];
        draft.request.fetch_chat = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.FETCH_CHAT_FAILURE:
        draft.request.fetch_chat = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.UPDATE_CHAT_REQUEST:
        draft.request.update_chat = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.UPDATE_CHAT_SUCCESS: {
        if (!draft.chats) draft.chats = [];
        const chat = result?.data || {};
        const idx = draft.chats.findIndex((c) => String(c.id) === String(chatId));
        if (idx !== -1) {
          draft.chats[idx].title = chat.title || chatTitle || draft.chats[idx].title;
        }
        draft.request.update_chat = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.UPDATE_CHAT_FAILURE:
        draft.request.update_chat = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.MOVE_CHAT_REQUEST:
        draft.request.move_chat = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.MOVE_CHAT_SUCCESS: {
        if (!draft.chats) draft.chats = [];
        const idx = draft.chats.findIndex((c) => String(c.id) === String(chatId));
        if (idx !== -1) {
          draft.chats[idx].central_ia_folder_id = targetFolderId;
        }
        draft.request.move_chat = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.MOVE_CHAT_FAILURE:
        draft.request.move_chat = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.UPLOAD_FILE_REQUEST:
        draft.request.upload_file = {
          loading: true,
          loaded: false,
          error: false,
        };
        break;
      case CentralIaTypes.UPLOAD_FILE_SUCCESS: {
        if (!draft.pendingFiles) draft.pendingFiles = [];
        const data = result?.data || {};
        if (data.file_id) {
          draft.pendingFiles.push({
            file_id: data.file_id,
            file_name: data.file_name,
            content_type: data.content_type,
            size_bytes: data.size_bytes,
            s3_url: data.s3_url,
          });
        }
        draft.request.upload_file = {
          loading: false,
          loaded: true,
          error: false,
        };
        break;
      }
      case CentralIaTypes.UPLOAD_FILE_FAILURE:
        draft.request.upload_file = {
          loading: false,
          loaded: false,
          error: true,
        };
        break;
      case CentralIaTypes.REMOVE_PENDING_FILE:
        if (!draft.pendingFiles) draft.pendingFiles = [];
        draft.pendingFiles = draft.pendingFiles.filter(
          (f) => String(f.file_id) !== String(payload?.fileId),
        );
        break;
      case CentralIaTypes.CLEAR_PENDING_FILES:
        draft.pendingFiles = [];
        break;
      case CentralIaTypes.REMOVE_CHAT_FILE:
        if (!draft.chatFiles) draft.chatFiles = [];
        draft.chatFiles = draft.chatFiles.filter(
          (f) => String(f.file_id) !== String(payload?.fileId),
        );
        break;
      case CentralIaTypes.CLEAR_CHAT:
        draft.messages = [];
        draft.selectedProvider = '';
        draft.selectedModel = '';
        draft.chatStarted = false;
        draft.sidebarCollapsed = false;
        draft.conversationId = null;
        draft.pendingFiles = [];
        draft.chatFiles = [];
        draft.lastError = null;
        draft.request.send_chat_message = REQUEST_INITIAL_STATE;
        break;
      default:
    }
  });
};

export default reducer;
